import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Linkedin, Mail, X } from "lucide-react";
import { useSiteContent } from "../data/content";
import logoGrey from "../data/pictures/Logo_grey.png";

export default function Footer() {
  const content = useSiteContent();
  const ui = content.ui.footer;
  const [modal, setModal] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const go = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <footer className="bg-[#08172C] text-slate-300">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={logoGrey}
                alt="Liesegang Consulting"
                className="h-20 w-auto object-contain brightness-0 invert opacity-90"
              />
              <div className="border-l border-white/15 pl-3">
                <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400 font-medium">
                  {content.brandTagline}
                </div>
              </div>
            </div>
            <p className="mt-6 text-sm text-slate-400 leading-relaxed max-w-md">{ui.description}</p>
            <div className="mt-6 flex items-center gap-3">
              <a href={content.contact.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href={`mailto:${content.contact.email}`} className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors" aria-label="Email">
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{ui.navTitle}</div>
            <ul className="mt-5 space-y-2.5 text-sm">
              {content.nav.map((n) => (
                <li key={n.id}>
                  <button onClick={() => go(n.id)} className="hover:text-white transition-colors">
                    {n.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{ui.contactTitle}</div>
            <ul className="mt-5 space-y-2.5 text-sm">
              <li>{content.contact.location}</li>
              <li>
                <a href={`mailto:${content.contact.email}`} className="hover:text-white">
                  {content.contact.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} {ui.copyright}</div>
          <div className="flex items-center gap-5">
            <button onClick={() => setModal("impressum")} className="hover:text-white transition-colors">
              {ui.impressum}
            </button>
            <button onClick={() => setModal("datenschutz")} className="hover:text-white transition-colors">
              {ui.datenschutz}
            </button>
          </div>
        </div>
      </div>

      {modal && (
        <LegalModal
          onClose={() => setModal(null)}
          closeLabel={ui.close}
          data={modal === "impressum" ? content.legal.impressum : content.legal.datenschutz}
        />
      )}
    </footer>
  );
}

function LegalModal({ data, onClose, closeLabel }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white text-slate-800 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-[#0B1F3A]">{data.title}</h3>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-slate-100" aria-label={closeLabel}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-6 overflow-y-auto space-y-6">
          {data.intro && <p className="text-sm text-slate-500 italic">{data.intro}</p>}
          {data.sections.map((s, i) => (
            <div key={i}>
              <h4 className="text-sm font-semibold text-[#0B1F3A] mb-2">{s.h}</h4>
              <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{s.p}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
